import {
  BillableItemIds,
  BillableItemPaymentModel,
  BillableItemStatus,
  BillableItemType,
  CurrencyCode,
  FeatureType
} from '../../enums/billableItem';
import { BillableItem, Feature } from '../../interfaces/billableItem';

const SystemContactLimit = (process.env.SystemContactLimit || 250000) as number;

const FreemiumFeatures: Feature[] = [
  { type: FeatureType.Automation, quantity: 1 },
  { type: FeatureType.HelpDocuments, quantity: 1 },
  { type: FeatureType.DynamicTemplate, quantity: 1 },
  { type: FeatureType.PictureMessaging, quantity: 1 },
  { type: FeatureType.EmailChatSupport, quantity: 1 },
  { type: FeatureType.SubscriberGrowthTool, quantity: 1 },
  { type: FeatureType.PersonalisedLandingPage, quantity: 1 },
  { type: FeatureType.CustomDomain, quantity: 1 },
  { type: FeatureType.MmsTextOverlay, quantity: 1 }
];

const StarterFeatures: Feature[] = [
  ...FreemiumFeatures,
  { type: FeatureType.TollFreeNumber, quantity: 1 },
  { type: FeatureType.OnBoardingLaunch, quantity: 1 },
  { type: FeatureType.BulkContactUpload, quantity: 1 },
  { type: FeatureType.ZapierIntegrations, quantity: 1 },
  {
    type: FeatureType.Contact,
    quantity: SystemContactLimit
  }
];

const GrowthFeatures: Feature[] = [
  ...StarterFeatures,
  { type: FeatureType.CustomIntegrations, quantity: 1 },
  { type: FeatureType.PriorityEmailLiveSupport, quantity: 1 },
  { type: FeatureType.UniqueVerifiedLocalNumber, quantity: 1 }
];

const ProFeatures: Feature[] = [
  ...GrowthFeatures,
  { type: FeatureType.PriorityCallSupport, quantity: 1 },
  { type: FeatureType.ApiAutomationCampaign, quantity: 1 }
];

export const monthlyPlans: BillableItem[] = [
  {
    id: BillableItemIds.Plan.FreeUsdMonthly,
    name: 'Pay As You Go',
    type: BillableItemType.Plan,
    priceInCents: 0,
    currencyCode: CurrencyCode.USD,
    paymentModel: BillableItemPaymentModel.Monthly,
    status: BillableItemStatus.Active,
    features: [
      ...FreemiumFeatures,
      {
        type: FeatureType.Contact,
        quantity: 150
      },
      {
        type: FeatureType.BundledSmsCreditCost,
        quantity: 150,
        priceInCents: 1
      },
      {
        type: FeatureType.PayAsYouGoSmsCreditCost,
        quantity: 1,
        priceInCents: 3
      }
    ]
  },
  {
    id: BillableItemIds.Plan.StarterUsdMonthly,
    name: 'Starter',
    type: BillableItemType.Plan,
    priceInCents: 5000,
    currencyCode: CurrencyCode.USD,
    paymentModel: BillableItemPaymentModel.Monthly,
    status: BillableItemStatus.Active,
    features: [
      ...StarterFeatures,
      { type: FeatureType.Sms, quantity: 2000 },
      {
        type: FeatureType.BundledSmsCreditCost,
        quantity: 2000,
        priceInCents: 2.5
      },
      {
        type: FeatureType.PayAsYouGoSmsCreditCost,
        quantity: 1,
        priceInCents: 2.5
      }
    ]
  },
  {
    id: BillableItemIds.Plan.GrowthUsdMonthly,
    name: 'Growth',
    type: BillableItemType.Plan,
    priceInCents: 10000,
    currencyCode: CurrencyCode.USD,
    paymentModel: BillableItemPaymentModel.Monthly,
    status: BillableItemStatus.Active,
    features: [
      ...GrowthFeatures,
      { type: FeatureType.Sms, quantity: 5000 },
      {
        type: FeatureType.BundledSmsCreditCost,
        quantity: 2,
        priceInCents: 2
      },
      {
        type: FeatureType.PayAsYouGoSmsCreditCost,
        quantity: 1,
        priceInCents: 2
      }
    ]
  },
  {
    id: BillableItemIds.Plan.ProUsdMonthly,
    name: 'Pro',
    type: BillableItemType.Plan,
    priceInCents: 22500,
    currencyCode: CurrencyCode.USD,
    paymentModel: BillableItemPaymentModel.Monthly,
    status: BillableItemStatus.Active,
    features: [
      ...ProFeatures,
      { type: FeatureType.Sms, quantity: 15000 },
      {
        type: FeatureType.BundledSmsCreditCost,
        quantity: 15000,
        priceInCents: 1.5
      },
      {
        type: FeatureType.PayAsYouGoSmsCreditCost,
        quantity: 1,
        priceInCents: 1.5
      }
    ]
  }
];

export const yearlyPlans: (BillableItem | undefined)[] = monthlyPlans
  .map(plan => {
    switch (plan.id) {
      case BillableItemIds.Plan.StarterUsdMonthly:
        return {
          ...plan,
          id: BillableItemIds.Plan.StarterUsdYearly,
          paymentModel: BillableItemPaymentModel.Yearly,
          priceInCents: 60000
        };
      case BillableItemIds.Plan.GrowthUsdMonthly:
        return {
          ...plan,
          id: BillableItemIds.Plan.GrowthUsdYearly,
          paymentModel: BillableItemPaymentModel.Yearly,
          priceInCents: 120000
        };
      case BillableItemIds.Plan.ProUsdMonthly:
        return {
          ...plan,
          id: BillableItemIds.Plan.ProUsdYearly,
          paymentModel: BillableItemPaymentModel.Yearly,
          priceInCents: 270000
        };
      default:
        return;
    }
  })
  .filter(Boolean);
